"use client";

import * as React from "react";
import type { Omittable } from "../../lib/props";

/**
 * The drag seams behind `<Box resize=... />`. Not exported on its own — a
 * resizable panel IS a Box, so the seam is part of Box's definition rather
 * than a component consumers mount by hand.
 *
 * The seam measures the Box at pointerdown instead of trusting a prop, so it
 * is correct whether the consumer drives the size through React state or
 * writes it straight to the element. Bounds come from the Box's own
 * `min-width`/`max-width`/`min-height`/`max-height` — the same CSS that would
 * cap the element anyway, so a drag can never hand back a size the layout
 * refuses to render.
 *
 * A corner handle drives both axes from one gesture. The size is all the seam
 * reports; a Box anchored by `left`/`top` that grows from its left or top edge
 * compensates its own position from the delta, which is the consumer's business
 * because only the consumer knows how it is anchored.
 */

/** Which part of the Box's perimeter carries a handle. The four edges drive one
 *  axis, the four corners drive both. */
export type ResizeEdge =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

/** The full box size after a drag step. An axis the handle does not own is
 *  reported as measured, so a consumer can persist both. */
export type ResizeSize = { width: number; height: number };

export type BoxResizeProps = Omittable<{
  /**
   * Handles that can be dragged. Box turns itself into a positioned container so
   * the handles anchor to its own perimeter, and clips nothing — each sits
   * INSIDE the edge it names, so a resizable Box never overflows its parent.
   *
   * Bounds are the Box's own `minWidth`/`maxWidth`/`minHeight`/`maxHeight`, read
   * from computed style, so any CSS unit works.
   */
  resize?: ResizeEdge | ResizeEdge[];
  /** Streams the next clamped size, at most once per animation frame while
   *  dragging and once per keyboard step. Drive the size you pass back in. */
  onResize?: (size: ResizeSize, edge: ResizeEdge) => void;
  onResizeStart?: (edge: ResizeEdge) => void;
  /** Fires once with the final size when the drag ends — persist here, not per
   *  frame. Also flushed if the Box unmounts mid-drag. */
  onResizeEnd?: (size: ResizeSize, edge: ResizeEdge) => void;
  /** Accessible name for the handles. Defaults to "Resize". */
  resizeLabel?: string;
  /**
   * Center each edge seam's indicator in the strip instead of flush against
   * the edge it names. For a seam whose strip OWNS the gutter between two
   * surfaces (the strip spans the gap, so the line should mark its middle)
   * rather than riding a panel's edge. Same themed indicator, same reveal.
   * Corner handles keep their bracket.
   */
  resizeCentered?: boolean;
}>;

/** Pointer travel toward +x/+y grows a box whose opposite edge is anchored, and
 *  shrinks one anchored at the edge being dragged. `0` means the handle does not
 *  own that axis. */
type Axes = { x: -1 | 0 | 1; y: -1 | 0 | 1 };

const AXES: Record<ResizeEdge, Axes> = {
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  top: { x: 0, y: -1 },
  bottom: { x: 0, y: 1 },
  "top-left": { x: -1, y: -1 },
  "top-right": { x: 1, y: -1 },
  "bottom-left": { x: -1, y: 1 },
  "bottom-right": { x: 1, y: 1 },
};

/** Arrow-key step, and the fine step held with Shift. */
const KEY_STEP = 16;
const KEY_STEP_FINE = 4;

// Above everything the panel itself stacks (floating action bars, pinned
// footers) — a seam you cannot reach is a seam that does not exist. Corners sit
// a rung above the edges they interrupt, so the two-axis gesture wins the
// overlap. The panel's own stacking context keeps both from climbing over
// unrelated UI.
const HIT = "absolute touch-none select-none group/seam";
const EDGE_HIT = `${HIT} z-60`;
const CORNER_HIT = `${HIT} z-70 size-3`;

const SEAM_CLASS: Record<ResizeEdge, string> = {
  left: `${EDGE_HIT} inset-y-0 left-0 w-2 cursor-col-resize`,
  right: `${EDGE_HIT} inset-y-0 right-0 w-2 cursor-col-resize`,
  top: `${EDGE_HIT} inset-x-0 top-0 h-2 cursor-row-resize`,
  bottom: `${EDGE_HIT} inset-x-0 bottom-0 h-2 cursor-row-resize`,
  "top-left": `${CORNER_HIT} top-0 left-0 cursor-nwse-resize`,
  "top-right": `${CORNER_HIT} top-0 right-0 cursor-nesw-resize`,
  "bottom-left": `${CORNER_HIT} bottom-0 left-0 cursor-nesw-resize`,
  "bottom-right": `${CORNER_HIT} bottom-0 right-0 cursor-nwse-resize`,
};

// Indicators paint the brand accent — never-neutral, so the seam reads as the
// accent blue under the neutral default theme instead of a black/white bar.
const INDICATOR =
  "pointer-events-none absolute bg-brand opacity-0 transition-opacity duration-150 group-hover/seam:opacity-100 group-focus-visible/seam:opacity-100 group-data-dragging/seam:opacity-100";
/** A corner reads as a bracket rather than a line: two 2px sides on the corner
 *  it owns, so the affordance says "both axes" without a glyph. */
const BRACKET =
  "pointer-events-none absolute inset-0.5 border-brand opacity-0 transition-opacity duration-150 group-hover/seam:opacity-100 group-focus-visible/seam:opacity-100 group-data-dragging/seam:opacity-100";

const LINE_CLASS: Record<ResizeEdge, string> = {
  left: `${INDICATOR} inset-y-0 left-0 w-0.5`,
  right: `${INDICATOR} inset-y-0 right-0 w-0.5`,
  top: `${INDICATOR} inset-x-0 top-0 h-0.5`,
  bottom: `${INDICATOR} inset-x-0 bottom-0 h-0.5`,
  "top-left": `${BRACKET} rounded-tl-sm border-t-2 border-l-2`,
  "top-right": `${BRACKET} rounded-tr-sm border-t-2 border-r-2`,
  "bottom-left": `${BRACKET} rounded-bl-sm border-b-2 border-l-2`,
  "bottom-right": `${BRACKET} rounded-br-sm border-r-2 border-b-2`,
};

/** The `resizeCentered` indicators: the same themed hover line, marking the
 *  middle of the strip instead of the edge it names. The line stops 16px
 *  short of the strip's ends with rounded caps — clear of the panels' own
 *  8px insets plus a step inward — so it reads as a handle floating in the
 *  gap rather than a divider bleeding to the container edge. The HIT strip
 *  still spans the full length. Edges only; a corner keeps its bracket. */
const CENTERED_LINE: Partial<Record<ResizeEdge, string>> = {
  left: `${INDICATOR} inset-y-4 left-1/2 w-0.5 -translate-x-1/2 rounded-full`,
  right: `${INDICATOR} inset-y-4 left-1/2 w-0.5 -translate-x-1/2 rounded-full`,
  top: `${INDICATOR} inset-x-4 top-1/2 h-0.5 -translate-y-1/2 rounded-full`,
  bottom: `${INDICATOR} inset-x-4 top-1/2 h-0.5 -translate-y-1/2 rounded-full`,
};

const CURSOR: Record<ResizeEdge, string> = {
  left: "col-resize",
  right: "col-resize",
  top: "row-resize",
  bottom: "row-resize",
  "top-left": "nwse-resize",
  "top-right": "nesw-resize",
  "bottom-left": "nesw-resize",
  "bottom-right": "nwse-resize",
};

/** `min-width: auto` and `max-width: none` both parse to NaN — that is the
 *  "unbounded" case, so each falls back to its own open end. */
function pxOr(value: string, fallback: number): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

type Baseline = {
  /** Pointer coordinates when the drag began. */
  originX: number;
  originY: number;
  width: number;
  height: number;
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
};

/** Measure the panel and resolve this drag's clamp bounds from its own CSS. */
function measure(panel: HTMLElement) {
  const rect = panel.getBoundingClientRect();
  const cs = getComputedStyle(panel);
  return {
    width: rect.width,
    height: rect.height,
    minWidth: pxOr(cs.minWidth, 0),
    maxWidth: pxOr(cs.maxWidth, Number.POSITIVE_INFINITY),
    minHeight: pxOr(cs.minHeight, 0),
    maxHeight: pxOr(cs.maxHeight, Number.POSITIVE_INFINITY),
  };
}

/**
 * While a seam is captured the pointer regularly outruns the strip, and the
 * cursor would flicker back to the default over whatever it crosses. Pinning it
 * on the document keeps one cursor for the whole gesture, and kills the text
 * selection a fast drag would otherwise paint across the page.
 */
function lockCursor(cursor: string): () => void {
  const { style } = document.body;
  const prevCursor = style.cursor;
  const prevSelect = style.userSelect;
  style.cursor = cursor;
  style.userSelect = "none";
  return () => {
    style.cursor = prevCursor;
    style.userSelect = prevSelect;
  };
}

/** The seam's splitter value: the panel's live extent along the axis the
 *  handle owns, plus the bounds the drag clamps to. An axis with no bound in
 *  the panel's own CSS reports none. */
type PanelExtent = {
  ref: React.RefCallback<HTMLDivElement>;
  now: number | undefined;
  min: number | undefined;
  max: number | undefined;
};

/**
 * Measure the panel this seam resizes.
 *
 * The seam renders as a direct child of that panel, so the panel is its
 * parent element — the same element the drag measures at pointerdown. A
 * `ResizeObserver` keeps the value current through a drag, a keyboard step
 * and any layout change the consumer never routed through `onResize`.
 */
function usePanelExtent(horizontal: boolean): PanelExtent {
  const [node, setNode] = React.useState<HTMLDivElement | null>(null);
  const [extent, setExtent] = React.useState<{
    now: number;
    min: number;
    max: number;
  } | null>(null);

  const ref = React.useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  React.useEffect(() => {
    const panel = node?.parentElement;
    if (!panel) return;
    const read = () => {
      const box = measure(panel);
      const next = {
        now: Math.round(horizontal ? box.width : box.height),
        min: Math.round(horizontal ? box.minWidth : box.minHeight),
        max: horizontal ? box.maxWidth : box.maxHeight,
      };
      setExtent((prev) =>
        prev &&
        prev.now === next.now &&
        prev.min === next.min &&
        prev.max === next.max
          ? prev
          : next,
      );
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [node, horizontal]);

  return {
    ref,
    now: extent?.now,
    min: extent?.min,
    max: extent && Number.isFinite(extent.max) ? Math.round(extent.max) : undefined,
  };
}

export function BoxResizeSeam({
  edge,
  label,
  centered = false,
  onResize,
  onResizeStart,
  onResizeEnd,
}: {
  edge: ResizeEdge;
  label: string;
  /** See {@link BoxResizeProps.resizeCentered}. */
  centered?: boolean | undefined;
} & Omittable<
  Pick<BoxResizeProps, "onResize" | "onResizeStart" | "onResizeEnd">
>): React.ReactElement {
  const axes = AXES[edge];
  const [dragging, setDragging] = React.useState(false);
  const extent = usePanelExtent(axes.x !== 0);

  const startRef = React.useRef<Baseline | null>(null);
  const lastRef = React.useRef<ResizeSize | null>(null);
  const frameRef = React.useRef(0);
  const pointRef = React.useRef({ x: 0, y: 0 });
  const unlockRef = React.useRef<(() => void) | null>(null);

  // Latest-ref: the handlers below are created once, but always see the
  // current callbacks.
  const cbRef = React.useRef({ onResize, onResizeStart, onResizeEnd });
  cbRef.current = { onResize, onResizeStart, onResizeEnd };

  /** Clamp both axes, remember, and stream one size. An axis this handle does
   *  not own passes through as measured. */
  const emit = React.useCallback(
    (rawWidth: number, rawHeight: number, start: Baseline) => {
      const size: ResizeSize = {
        width: axes.x
          ? Math.min(start.maxWidth, Math.max(start.minWidth, rawWidth))
          : start.width,
        height: axes.y
          ? Math.min(start.maxHeight, Math.max(start.minHeight, rawHeight))
          : start.height,
      };
      lastRef.current = size;
      cbRef.current.onResize?.(size, edge);
      return size;
    },
    [axes.x, axes.y, edge],
  );

  const finish = React.useCallback(() => {
    if (!startRef.current) return;
    startRef.current = null;
    cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;
    unlockRef.current?.();
    unlockRef.current = null;
    setDragging(false);
    if (lastRef.current) cbRef.current.onResizeEnd?.(lastRef.current, edge);
  }, [edge]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const panel = e.currentTarget.parentElement;
    if (!panel) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startRef.current = {
      originX: e.clientX,
      originY: e.clientY,
      ...measure(panel),
    };
    lastRef.current = null;
    unlockRef.current = lockCursor(CURSOR[edge]);
    setDragging(true);
    cbRef.current.onResizeStart?.(edge);
  };

  // Pointer events fire faster than the screen repaints; coalescing to one
  // frame means one clamp + one consumer render per painted frame.
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return;
    pointRef.current = { x: e.clientX, y: e.clientY };
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      const start = startRef.current;
      if (!start) return;
      emit(
        start.width + (pointRef.current.x - start.originX) * axes.x,
        start.height + (pointRef.current.y - start.originY) * axes.y,
        start,
      );
    });
  };

  // Arrow keys step the axis they point along, Home/End jump to the bounds of
  // every axis this handle owns. Each press is a discrete commit, so a keyboard
  // resize persists like a released drag.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const panel = e.currentTarget.parentElement;
    if (!panel) return;
    const start: Baseline = { originX: 0, originY: 0, ...measure(panel) };
    const step = e.shiftKey ? KEY_STEP_FINE : KEY_STEP;
    let width = start.width;
    let height = start.height;
    if (e.key === "ArrowLeft" && axes.x) width -= step * axes.x;
    else if (e.key === "ArrowRight" && axes.x) width += step * axes.x;
    else if (e.key === "ArrowUp" && axes.y) height -= step * axes.y;
    else if (e.key === "ArrowDown" && axes.y) height += step * axes.y;
    else if (e.key === "Home") {
      if (axes.x) width = start.minWidth;
      if (axes.y) height = start.minHeight;
    } else if (e.key === "End") {
      if (axes.x) width = start.maxWidth;
      if (axes.y) height = start.maxHeight;
    } else return;
    // Only an owned axis has to be finite. An unbounded `max-height` on a
    // width-only handle is normal, and checking it would swallow every End.
    if (
      (axes.x && !Number.isFinite(width)) ||
      (axes.y && !Number.isFinite(height))
    ) {
      return;
    }
    e.preventDefault();
    // Emit first: as an argument to an optional call, `emit` would be skipped
    // entirely whenever the consumer omits `onResizeEnd`, and the keyboard step
    // would stream nothing.
    const size = emit(width, height, start);
    cbRef.current.onResizeEnd?.(size, edge);
  };

  // A seam unmounted mid-drag (its dialog closed, the pane collapsed) still
  // ends the gesture: release the cursor lock and flush the final size so
  // persistence always runs.
  React.useEffect(() => finish, [finish]);

  return (
    <div
      aria-label={label}
      // A corner belongs to no single axis, so it reports none rather than
      // claiming one.
      aria-orientation={
        axes.x && axes.y ? undefined : axes.x ? "vertical" : "horizontal"
      }
      // A focusable separator is a window splitter, and a splitter reports the
      // size it moves. The panel's own px extent along the axis this handle
      // owns IS that value, and the bounds are the clamp the drag already
      // obeys. An open end publishes no bound rather than a made-up one.
      aria-valuemax={extent.max}
      aria-valuemin={extent.min}
      aria-valuenow={extent.now}
      className={SEAM_CLASS[edge]}
      data-dragging={dragging || undefined}
      data-slot="box-resize-seam"
      onKeyDown={onKeyDown}
      onLostPointerCapture={finish}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      ref={extent.ref}
      role="separator"
      tabIndex={0}
    >
      <div className={(centered && CENTERED_LINE[edge]) || LINE_CLASS[edge]} />
    </div>
  );
}
